import React from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Image,
  Flex,
  Badge,
  useColorModeValue,
  Divider,
  Icon,
  Circle,
  Button,
} from '@chakra-ui/react';
import { FaHeadphones, FaCode, FaTools, FaArrowRight } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';

const About = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const subtleBg = useColorModeValue('blue.50', 'blue.900');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  const baseUrl = "https://abhishekbhalerao.com";
  
  return (

    <>
      <SEO 
        title="About | Abhishek Bhalerao - Full Stack Developer & Robotics Engineer"
        description="Learn about my journey from audio engineering to software development and robotics. Discover my skills, expertise, and professional background."
        keywords="Abhishek Bhalerao, Full Stack Developer, Industrial Automation, Robotics Engineering, Software Development, Audio Engineering, Professional Background"
        ogImage={`${baseUrl}/images/abhishek_bhalerao_og.jpg`}
        twitterImage={`${baseUrl}/images/abhishek_bhalerao_og.jpg`}
        canonicalUrl={`${baseUrl}/about`}
      />

    <Container maxW="container.xl" py={8} mt="120px">
      {/* Header with decorative elements */}
      <Box position="relative" mb={12} textAlign="center">
        <Box
          position="absolute"
          top="-30px"
          left="50%"
          transform="translateX(-50%)"
          width="200px"
          height="200px"
          borderRadius="full"
          bg={`radial-gradient(circle, ${useColorModeValue('blue.100', 'blue.700')} 0%, transparent 70%)`}
          opacity="0.6"
          zIndex="-1"
        />

        <Heading as="h1" size="2xl" mb={8} textAlign="center">
            About Me
        </Heading>

        <Divider my={6} maxW="100px" borderWidth="2px" borderColor="blue.500" mx="auto" />

        <Text 
          fontSize="xl" 
          fontWeight="medium" 
          color={accentColor} 
          maxW="700px"
          mx="auto"
          lineHeight="tall"
        >
          Harmonizing Audio, Software, and Robotics Engineering
        </Text>
      </Box>
      
      {/* Journey Section with Enhanced Image */}
      <Box 
        mb={16} 
        p={6} 
        borderRadius="xl" 
        bg={bgColor} 
        boxShadow="lg"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
          <Box>
            <Box 
              position="relative" 
              maxW="300px" 
              mx="auto"
              _before={{
                content: '""',
                position: "absolute",
                top: "-15px",
                left: "-15px",
                right: "15px",
                bottom: "15px",
                borderRadius: "lg",
                background: "linear-gradient(135deg, #3182CE 0%, #2C5282 100%)",
                zIndex: "-1",
              }}
            >
              <Image
                src="/images/abhishek_bhalerao_memoji.jpeg"
                alt="Abhishek Bhalerao"
                borderRadius="lg"
                boxShadow="xl"
                maxH="300px"
                objectFit="cover"
                border="4px solid white"
              />
            </Box>
          </Box>
          
          <VStack align="start" spacing={6}>
            <Heading 
              as="h2" 
              size="lg"
              position="relative"
              _after={{
                content: '""',
                position: "absolute",
                bottom: "-8px",
                left: "0",
                width: "40px",
                height: "4px",
                borderRadius: "full",
                bg: "blue.500",
              }}
            >
              My Journey
            </Heading>
            <Text fontSize="md" lineHeight="tall">
              From crafting pristine audio experiences in professional studios to developing industrial automation systems 
              and engineering robotic solutions - my journey weaves through sound, code, and precision machinery. 
              Digital signal processing is where my audio engineering meets programming. I can not only hear what good audio should sound like, 
              but also code the algorithms to achieve it. This unique blend allows me to bring a multifaceted perspective to every project.
            </Text>
            
            <Box 
              p={4} 
              bg={subtleBg} 
              borderRadius="md" 
              borderLeft="4px solid" 
              borderColor="blue.500"
              mt={4}
            >
              <Text fontStyle="italic" fontSize="sm">
                "The intersection of audio, software, and hardware is where innovation truly happens. 
                It's about creating solutions that not only function flawlessly but also provide an exceptional user experience."
              </Text>
            </Box>
          </VStack>
        </SimpleGrid>
      </Box>
      
      <Divider 
        my={10} 
        borderWidth="2px" 
        borderColor={borderColor} 
        width="100px" 
        mx="auto" 
        opacity="0.6" 
      />
      
      {/* Expertise Section with Icons */}
      <Box mb={10}>
        <Heading 
          as="h2" 
          size="lg" 
          mb={10} 
          textAlign="center"
          position="relative"
          _after={{
            content: '""',
            position: "absolute",
            bottom: "-10px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "60px",
            height: "4px",
            borderRadius: "full",
            bg: "blue.500",
          }}
        >
          Areas of Expertise
        </Heading>
        
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          <Box
            p={6}
            borderRadius="lg"
            bg={bgColor}
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            transition="transform 0.2s"
            _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
          >
            <Circle size="50px" bg="blue.500" color="white" mb={4}>
              <Icon as={FaHeadphones} w={6} h={6} />
            </Circle>
            <Heading as="h3" size="md" mb={4} color="blue.500">
              Audio Engineering
            </Heading>
            <VStack align="start" spacing={2}>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Sound Design</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Audio Post-Production</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Audio Mastering</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Digital Signal Processing</Badge>
            </VStack>
          </Box>
          
          <Box
            p={6}
            borderRadius="lg"
            bg={bgColor}
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            transition="transform 0.2s"
            _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
          >
            <Circle size="50px" bg="blue.500" color="white" mb={4}>
              <Icon as={FaCode} w={6} h={6} />
            </Circle>
            <Heading as="h3" size="md" mb={4} color="blue.500">
              Development
            </Heading>
            <Flex wrap="wrap" gap={2}>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Python</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">JavaScript</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Node.js</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">MySQL</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">React.js</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Express.js</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">MongoDB</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Git</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">REST APIs</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">HTML5</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">CSS3</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Bootstrap</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Tailwind CSS</Badge>
            </Flex>
          </Box>
          
          <Box
            p={6}
            borderRadius="lg"
            bg={bgColor}
            boxShadow="md"
            borderWidth="1px"
            borderColor={borderColor}
            transition="transform 0.2s"
            _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
          >
            <Circle size="50px" bg="blue.500" color="white" mb={4}>
              <Icon as={FaTools} w={6} h={6} />
            </Circle>
            <Heading as="h3" size="md" mb={4} color="blue.500">
              Professional Tools & Software
            </Heading>
            <VStack align="start" spacing={2}>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">AVID | Pro Tools</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Steinberg | Nuendo</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Steinberg | Cubase</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Visual Studio Code</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Git & GitHub</Badge>
              <Badge colorScheme="blue" px={2} py={1} borderRadius="md">Docker</Badge>
            </VStack>
          </Box>
        </SimpleGrid>
      </Box>

      {/* Call to Action - New Section */}
      <Box
        mt={16}
        position="relative"
        overflow="hidden"
        borderRadius="lg"
        boxShadow="xl"
      >
        {/* Background with gradient and pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blue.600"
          backgroundImage="linear-gradient(135deg, #3182CE 0%, #2C5282 100%)"
          zIndex="0"
        />
        
        {/* Decorative elements */}
        <Box
          position="absolute"
          top="-20px"
          right="-20px"
          width="150px"
          height="150px"
          borderRadius="full"
          bg="whiteAlpha.200"
          zIndex="0"
        />
        <Box
          position="absolute"
          bottom="-30px"
          left="-30px"
          width="120px"
          height="120px"
          borderRadius="full"
          bg="whiteAlpha.100"
          zIndex="0"
        />
        
        {/* Content */}
        <Flex
          direction={{ base: "column", md: "row" }}
          align="center"
          justify="space-between"
          p={{ base: 8, md: 10 }}
          position="relative"
          zIndex="1"
        >
          <Box maxW={{ base: "100%", md: "60%" }} mb={{ base: 6, md: 0 }}>
            <Heading 
              as="h2" 
              size="lg" 
              color="white" 
              mb={4}
              fontWeight="bold"
            >
              Interested in My Unique Skill Set?
            </Heading>
            <Text color="whiteAlpha.900" fontSize="md" maxW="500px">
              My diverse background in audio, software, and robotics enables me to approach problems from multiple angles.
              Check out my projects to see how I blend these disciplines to create innovative solutions.
            </Text>
          </Box>
          
          <Box>
            <Button
              as={RouterLink}
              to="/projects"
              size="lg"
              bg="white"
              color="blue.600"
              _hover={{ bg: "gray.100" }}
              rightIcon={<FaArrowRight />}
              boxShadow="md"
              fontWeight="medium"
              px={8}
            >
              View My Projects
            </Button>
          </Box>
        </Flex>
      </Box>
    </Container>
    </>
  );
};

export default About;








